import { useState, useCallback } from 'react';

/**
 * Hook to manage edit warning dialog state with sessionStorage
 * 
 * Ensures the warning is shown only once per session per view.
 * Each view should use a unique storageKey to track its own warning state.
 * 
 * @param storageKey - Unique key for sessionStorage (e.g., 'editWarningShown_companies')
 * @param onProceed - Callback to execute when user proceeds with edit
 * @returns Object with showWarning state and handlers
 */
export function useEditWarning(
  storageKey: string,
  onProceed: () => void
) {
  const [showWarning, setShowWarning] = useState(false);

  /**
   * Check if warning should be shown and handle edit mode entry
   */
  const handleEditClick = useCallback(() => {
    // Check if warning has already been shown in this session
    const warningShown = sessionStorage.getItem(storageKey) === 'true';
    
    if (warningShown) {
      // Warning already shown, proceed directly
      onProceed();
    } else {
      // Show warning for first time
      setShowWarning(true);
    }
  }, [storageKey, onProceed]);

  /**
   * Handle user proceeding after seeing warning
   */
  const handleProceed = useCallback(() => {
    // Mark warning as shown for this session
    sessionStorage.setItem(storageKey, 'true');
    setShowWarning(false);
    onProceed();
  }, [storageKey, onProceed]);

  /**
   * Handle user canceling the warning
   */
  const handleCancel = useCallback(() => {
    setShowWarning(false);
  }, []);

  return {
    showWarning,
    handleEditClick,
    handleProceed,
    handleCancel,
  };
}

